/**
 * Ship designs as portable text.
 *
 * Three jobs, one format: authoring the AI ships that ship with the game,
 * letting players trade designs with each other, and saving your own work
 * between sessions.
 *
 * That third audience is why this validates rather than trusting. A design that
 * arrives from another player is untrusted input — it can be hand-edited,
 * truncated, or built by a version that disagreed about what a component is —
 * and a malformed one must come back as a clear refusal, never as a half-built
 * ship with dangling references that the compartment tracer walks off the end
 * of.
 *
 * The format is deliberately plain: a design is small, and being able to read
 * and hand-edit one is worth more than the bytes a compact encoding would save.
 */
import { COMPONENT_TYPES } from '../components.js';
import { DEFAULT_LINE_COLOR } from './upgrades.js';
/**
 * Bumped when the shape of a saved design changes incompatibly.
 *
 * 1 → 2: ships and stations halved in size so the world would read as bigger.
 * Designs are stored in world units, so every existing hull became twice the
 * size of the pad it was built on. Reading one scales it, rather than refusing
 * it — a scale change is our decision, and losing someone's ship over it would
 * be indefensible.
 */
export const DESIGN_FORMAT = 3;
/**
 * Component types that have been renamed, and what they are now.
 *
 * A design is a list of type ids, so renaming one orphans every hull that used
 * it. Cheaper to carry the old name here than to ask anyone to redraw: the
 * table is two lines, and it means an id can be improved without the change
 * costing somebody their ship.
 */
const RENAMED_COMPONENTS = {
    weapon: 'turretSmall',
};
/** How much to shrink a design written before the halving. */
const FORMAT_1_SCALE = 0.5;
export function exportDesign(name, graph) {
    const design = { format: DESIGN_FORMAT, name, graph };
    return JSON.stringify(design, null, 2);
}
const isFinitePair = (value) => {
    const point = value;
    return (!!point &&
        typeof point.x === 'number' && Number.isFinite(point.x) &&
        typeof point.y === 'number' && Number.isFinite(point.y));
};
/**
 * Read a design, or say exactly why it cannot be read.
 *
 * Every reference is checked to point at something that exists. A line naming a
 * joint that is not in the file is the failure that matters here: it survives a
 * shallow type check and then crashes the face tracer, which is a long way from
 * where the bad data came in.
 */
export function importDesign(source) {
    let parsed;
    try {
        parsed = JSON.parse(source);
    }
    catch {
        return { ok: false, error: 'Not valid JSON.' };
    }
    const design = parsed;
    if (!design || typeof design !== 'object')
        return { ok: false, error: 'Not a ship design.' };
    // Older formats are migrated, not refused. Anything newer cannot be, since
    // this build has no idea what was added.
    const format = design.format;
    if (typeof format !== 'number' || format > DESIGN_FORMAT || format < 1) {
        return {
            ok: false,
            error: `Design format ${String(format)} — this build reads up to format ${DESIGN_FORMAT}.`,
        };
    }
    const scale = format === 1 ? FORMAT_1_SCALE : 1;
    const graph = design.graph;
    if (!graph || !Array.isArray(graph.joints) || !Array.isArray(graph.lines) || !Array.isArray(graph.components)) {
        return { ok: false, error: 'Design is missing its joints, lines or components.' };
    }
    const joints = [];
    const seenJoints = new Set();
    for (const raw of graph.joints) {
        const joint = raw;
        const id = joint.id;
        if (typeof id !== 'string' || !isFinitePair(joint)) {
            return { ok: false, error: 'A joint is missing its id or position.' };
        }
        if (seenJoints.has(id))
            return { ok: false, error: `Duplicate joint ${id}.` };
        seenJoints.add(id);
        joints.push({ id, x: joint.x * scale, y: joint.y * scale });
    }
    const lines = [];
    for (const raw of graph.lines) {
        const line = raw;
        if (typeof line.id !== 'string' || typeof line.a !== 'string' || typeof line.b !== 'string') {
            return { ok: false, error: 'A line is missing its id or endpoints.' };
        }
        // The check worth having: a dangling reference passes a shallow validation
        // and then breaks the face tracer far from here.
        if (!seenJoints.has(line.a) || !seenJoints.has(line.b)) {
            return { ok: false, error: `Line ${line.id} refers to a joint that is not in the design.` };
        }
        if (typeof line.length !== 'number' || !Number.isFinite(line.length) || line.length <= 0) {
            return { ok: false, error: `Line ${line.id} has no usable length.` };
        }
        lines.push({
            id: line.id,
            a: line.a,
            b: line.b,
            length: line.length * scale,
            health: typeof line.health === 'number' ? line.health : 10,
            strength: typeof line.strength === 'number' ? line.strength : 1,
            color: typeof line.color === 'string' ? line.color : DEFAULT_LINE_COLOR,
        });
    }
    const components = [];
    for (const raw of graph.components) {
        const component = raw;
        if (typeof component.id !== 'string' || !isFinitePair(component.position)) {
            return { ok: false, error: 'A component is missing its id or position.' };
        }
        const renamed = RENAMED_COMPONENTS[component.type];
        const type = renamed ?? component.type;
        if (!COMPONENT_TYPES.includes(type)) {
            return { ok: false, error: `Unknown component type "${String(component.type)}".` };
        }
        components.push({
            id: component.id,
            type,
            position: { x: component.position.x * scale, y: component.position.y * scale },
            // Wrapped rather than rejected: an out-of-range facing is a rounding
            // artefact or an older file, not an attack, and it has an obvious reading.
            facing: ((Math.round(Number(component.facing ?? 0)) % 8) + 8) % 8,
            mirrored: component.mirrored === true,
        });
    }
    return {
        ok: true,
        design: {
            format: DESIGN_FORMAT,
            name: typeof design.name === 'string' && design.name.trim() ? design.name : 'Untitled',
            graph: { joints, lines, components },
        },
    };
}
//# sourceMappingURL=design.js.map